import { getCookie, removeCookie, setCookie } from '@/features/shared/utils/cookies'
import { type RequestCookies } from '@shared/request_type'

/**
 * Session cookie that stores the id of the user group the customer has
 * currently selected to shop as. A user may belong to zero or many groups
 * (see `usePersona`), but only one is "active" at a time. Eventually
 * this selection will be persisted on the customer object on the backend,
 * but until that's available we persist the selected group id in a
 * JS-readable session cookie so it is cleared when the browser session ends.
 *
 * Group id values are placeholders:
 *   - "0"   personal
 *   - "123" business
 */
export const SELECTED_USER_GROUP_COOKIE = 's_group'

/**
 * This is a sentinel value; it does not correspond to an actual user group,
 * but rather indicates that the user is shopping with their "personal" profile
 * (i.e. not impersonating any of their business groups). We use this value in
 * the cookie to be able to distinguish between a user that has explicitly
 * selected their personal profile vs. a user that has not made any
 * selection yet (in which case there would be no cookie).
 */
export const GROUP_ID_PERSONAL = '0'

/**
 * Reads the selected user group id from a server-provided cookies bag
 * (e.g. `ctx.req.cookies` in `getServerSideProps` or the cookies map
 * passed into app-router layouts). Safe to call on both client and server.
 */
export const getSelectedUserGroupIdFromCookies = (
  cookies: RequestCookies | undefined,
): string | undefined => cookies?.[SELECTED_USER_GROUP_COOKIE]

/**
 * Client-only read of the currently selected user group id cookie.
 * Returns `undefined` on the server – server code should use
 * {@link getSelectedUserGroupIdFromCookies} instead, or read the value
 * from the `SelectedUserGroupContext`.
 */
export const getSelectedUserGroupIdCookie = (): string | undefined => {
  const value = getCookie(SELECTED_USER_GROUP_COOKIE)
  // `getCookie` JSON-parses values (so `'0'` would become `0`) and returns
  // `null`/`undefined` when missing or on the server. Normalize back to
  // `string | undefined`.
  if (typeof value === 'undefined' || value === null) {
    return
  }
  return String(value)
}

/**
 * Shared cookie attributes for the selected user group cookie. Both the write
 * and remove helpers must use the same `path`/`sameSite` so the browser
 * identifies the cookie consistently when clearing it. Not HttpOnly so it
 * remains JS-accessible; `sameSite: 'lax'` allows the cookie to be seen in
 * server renders on top-level navigation.
 */
const SELECTED_USER_GROUP_COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'lax',
} as const

/**
 * Number of days the selected user group cookie persists before expiring.
 */
const SELECTED_USER_GROUP_COOKIE_EXPIRES_DAYS = 30

export const writeSelectedUserGroupIdCookie = (value: string) => {
  // Setting cookie to root path, with a 30 day expiration. Not HttpOnly so it remains JS-accessible.
  // sameSite lax allows the cookie to be seen in server renders on top-level navigation
  // `setCookie` is a no-op on the server and swallows errors (e.g. cookies
  // disabled); the provider set function is still called for local context
  // update.
  setCookie(SELECTED_USER_GROUP_COOKIE, value, {
    ...SELECTED_USER_GROUP_COOKIE_OPTIONS,
    expires: SELECTED_USER_GROUP_COOKIE_EXPIRES_DAYS,
  })
}

/**
 * Removes the selected user group id cookie. No-op on the server.
 */
export const removeSelectedUserGroupIdCookie = () => {
  removeCookie(SELECTED_USER_GROUP_COOKIE, SELECTED_USER_GROUP_COOKIE_OPTIONS)
}
